import { Command } from 'commander';
import { getV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { V2ApiError } from '../../api/v2-errors.js';
import type { IV2Client } from '../../api/v2-client.js';
import { addGridOption, gridSlug, type GridOpts } from '../../grid-opt.js';

export async function resolveSpaceIdBySlug(client: IV2Client, slug: string): Promise<string> {
  const { spaces } = await client.get<{ spaces: Array<{ space_id: string; slug: string }> }>('/api/v2/spaces');
  const match = spaces.find((s) => s.slug === slug);
  if (!match) throw new V2ApiError(404, 'SPACE_NOT_FOUND', `No space called "${slug}" in the active grid.`);
  return match.space_id;
}

interface ShowOpts extends GridOpts { json?: boolean; }

export async function runSpaceShow(slug: string, opts: ShowOpts, client?: IV2Client): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const c = client ?? await getV2Client(gridSlug(opts));
  const id = await resolveSpaceIdBySlug(c, slug);
  const detail = await c.get<{
    slug: string;
    member_count: number;
    description?: string;
    members?: Array<{ name: string; slug: string; role: string }>;
  }>(`/api/v2/spaces/${id}`);
  out.json(() => detail);
  out.human(() => {
    const lines = [`${detail.slug} · ${detail.member_count} member${detail.member_count === 1 ? '' : 's'}`];
    if (detail.description) lines.push(`Description: ${detail.description}`);
    lines.push('Members:');
    for (const m of detail.members ?? []) lines.push(`  - ${m.name} (${m.slug}) — ${m.role}`);
    return lines.join('\n');
  });
}

export const spaceShowCommand = new Command('show')
  .description('Show space details')
  .argument('<slug>', 'Space slug')
  .option('--json', 'JSON output');
addGridOption(spaceShowCommand, 'Override active grid');
spaceShowCommand
  .action(async (slug: string, opts: ShowOpts) => {
    try { await runSpaceShow(slug, opts); }
    catch (err) { await handleV2Error(err, { json: !!opts.json }); }
  });
